import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import MarketIntelligenceInsights from '../components/MarketIntelligenceInsights'
import {
  TrendingUp,
  Search,
  Lightbulb,
  AlertCircle,
  CheckCircle2,
  Plus,
  PieChart,
  Users,
  Zap,
  ArrowUp,
  ArrowDown,
  Minus,
  Loader2,
  MapPin,
  Globe,
  Building2
} from 'lucide-react'

// Demo data - Industry-level with brand performance (fallback)
const DEMO_DATA = {
  industry: {
    name: 'Consumer Goods',
    slug: 'consumer-goods'
  },
  // Demo: shows as national market
  location: null,
  isLocalized: false,
  marketOverview: {
    // Industry-wide metrics (shared across all brands)
    estimatedMonthlySearches: '~2.5M',
    marketSize: '~$850B annual revenue',
    totalMarketQueries: 50,
    marketTrend: 'Growing',
    trendPercentage: '+8%',
    lastUpdated: '2025-11-12'
  },
  brandPerformance: {
    // Brand-specific metrics
    brandName: 'Acme Snacks',
    coveragePercentage: 32, // % of industry queries you track
    industryCoverageAverage: 28, // Industry average
    trackedQueries: 16,
    untrackedQueries: 34,
    mentionRate: 45, // % of tracked queries where you're mentioned
    industryMentionRateAverage: 38,
    rankInIndustry: 8,
    totalBrandsInIndustry: 52
  },
  marketPosition: {
    mentionedInResearch: true,
    summary: 'Acme Snacks is recognized as an emerging player in the organic snacks market, known for clean ingredients and sustainable practices. While competing against established brands like NatureBox and Graze, Acme Snacks has carved out a niche with families seeking transparent sourcing and eco-friendly packaging.',
    marketPosition: 'Emerging Growth Brand',
    marketShare: '3-5%',
    brandRecognition: 'Growing Awareness',
    competitiveStanding: 'Positioned as a premium organic snack brand with strong values alignment. Competes directly with NatureBox and Graze in the subscription box segment, with differentiation through sustainability and ingredient transparency. Growing market share among health-conscious millennial parents.',
    keyDifferentiators: 'Certified organic ingredients, compostable packaging, transparent supply chain, family-focused product development, allergen-free options. Strong emphasis on sustainability sets Acme Snacks apart from traditional competitors who focus primarily on convenience.',
    researchedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString() // 1 week ago
  },
  topMarketQueries: [
    { query: 'best eco-friendly household products', volume: 'High', buyingSignal: 'Informational', tracked: false, priority: 'High' },
    { query: 'sustainable consumer goods brands comparison', volume: 'High', buyingSignal: 'Comparison', tracked: false, priority: 'High' },
    { query: 'affordable organic household essentials', volume: 'Medium', buyingSignal: 'Informational', tracked: false, priority: 'Medium' },
    { query: 'where to buy natural cleaning products', volume: 'High', buyingSignal: 'Transactional', tracked: false, priority: 'High' },
    { query: 'non-toxic consumer goods for families', volume: 'Medium', buyingSignal: 'Informational', tracked: false, priority: 'Medium' },
    { query: 'consumer goods subscription boxes', volume: 'Medium', buyingSignal: 'Comparison', tracked: false, priority: 'Medium' },
    { query: 'best value household products bulk buy', volume: 'Medium', buyingSignal: 'Transactional', tracked: false, priority: 'Low' },
    { query: 'plastic-free consumer goods alternatives', volume: 'Low', buyingSignal: 'Informational', tracked: false, priority: 'Low' }
  ],
  buyingSignals: [
    { signal: 'Informational', percentage: 48, count: 24, description: 'Users learning about eco-friendly options' },
    { signal: 'Comparison', percentage: 32, count: 16, description: 'Users comparing sustainable brands' },
    { signal: 'Transactional', percentage: 20, count: 10, description: 'Users ready to purchase' }
  ],
  competitiveGaps: [
    { query: 'sustainable packaging consumer goods guide', competitors: ['Seventh Generation', 'Method'], tracked: false },
    { query: 'zero waste household products', competitors: ['Grove Collaborative', 'Blueland'], tracked: false },
    { query: 'best refillable cleaning products', competitors: ['Branch Basics', 'Common Good'], tracked: false }
  ],
  marketTrends: [
    { trend: 'Sustainability & eco-friendly products', direction: 'rising', change: '+52%', description: 'Massive shift toward sustainable consumer goods' },
    { trend: 'Refillable & zero-waste options', direction: 'rising', change: '+38%', description: 'Plastic reduction driving refill systems' },
    { trend: 'Premium organic products', direction: 'rising', change: '+24%', description: 'Health-conscious consumers willing to pay more' },
    { trend: 'Generic/conventional products', direction: 'declining', change: '-15%', description: 'Traditional brands losing market share' }
  ],
  journeyInsights: {
    averageResearchDuration: '1-2 weeks',
    topDecisionFactors: ['Sustainability', 'Price', 'Brand values', 'Product effectiveness', 'Ingredient transparency'],
    pricesensitivity: 'Medium',
    urgencyLevel: 'Low'
  }
}

export default function MarketIntelligence() {
  const { activeBrand, currentUser } = useAuth()
  const { isDemoMode } = useDemoMode()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [marketData, setMarketData] = useState<any>(null)
  const [generatingReport, setGeneratingReport] = useState(false)
  const [industryId, setIndustryId] = useState<string | null>(null)
  const [isLocalBusiness, setIsLocalBusiness] = useState(false)

  // Helper function to extract citation numbers from text
  const extractCitationNumbers = (text: string): number[] => {
    if (!text) return []
    const matches = text.match(/\[(\d+)\]/g)
    if (!matches) return []
    return [...new Set(matches.map(m => parseInt(m.replace(/\[|\]/g, ''))))]
  }

  // Helper function to get sources for specific text based on citation numbers
  const getSourcesForText = (...texts: string[]): string[] => {
    if (!data.sources || data.sources.length === 0) return []

    // Combine all citation numbers from all texts
    const allCitationNumbers = texts.flatMap(text => extractCitationNumbers(text))
    const uniqueNumbers = [...new Set(allCitationNumbers)]

    // Map citation numbers to actual sources (1-indexed)
    return uniqueNumbers
      .map(num => data.sources[num - 1])
      .filter(Boolean)
  }

  // Helper function to strip citation markers like [1], [2], (Source Name)[3], and parenthetical sources
  // Keeps year ranges like (2025-2032) but removes source attributions
  const stripCitations = (text: string): string => {
    if (!text) return text
    return text
      .replace(/\[\d+\]/g, '') // Remove [1], [2], [3]
      .replace(/\(([^)]*?)(\d{4}-\d{4})([^)]*?)\)/g, '($2)') // Keep year ranges, remove surrounding text in parens
      .replace(/\([^)]+\)/g, '') // Remove any remaining parenthetical content (source names)
      .replace(/\s+(per|according to|via|from|based on)\s+[^,\n]+(\d{4})/gi, '') // Remove inline source attributions like "per SEMrush & Google Trends 2025"
      .replace(/,\s*$/, '') // Remove trailing commas
      .trim()
  }

  // Poll for market report generation status
  const pollMarketReportStatus = async (): Promise<boolean> => {
    const maxAttempts = 60 // 5 minutes (5s intervals)
    let attempts = 0

    while (attempts < maxAttempts) {
      try {
        // Check v1 status endpoint which tracks actual job progress
        const statusResponse = await api.get(`/v1/market-analysis/status`)
        const { status, progress } = statusResponse.data

        console.log(`[MarketIntelligence] Poll ${attempts + 1}: status=${status}, progress=${progress}%`)

        if (status === 'completed') {
          return true
        }

        if (status === 'failed') {
          throw new Error(statusResponse.data.error || 'Market analysis failed')
        }

        // Still processing - wait and try again
        await new Promise(resolve => setTimeout(resolve, 5000))
        attempts++
      } catch (error: any) {
        console.error('[MarketIntelligence] Poll error:', error)
        throw error
      }
    }

    throw new Error('Market analysis timed out')
  }

  // Function to generate market research report
  const generateMarketReport = async () => {
    console.log('Generate Market Report clicked', { industryId, currentUser, activeBrand, isLocalBusiness })

    if (!industryId) {
      console.error('Missing required data:', { industryId })
      alert('Cannot generate report: No industry ID')
      return
    }

    try {
      setGeneratingReport(true)
      console.log('Calling API to generate report...')

      // Use different endpoint based on business type
      let response
      if (isLocalBusiness && activeBrand?.id) {
        // Local business - generate localized market research
        response = await api.post(`/market-intelligence/brands/${activeBrand.id}/generate-localized`, {
          userId: currentUser?.id || null,
          userBrandId: activeBrand?.userBrandId || null
        })
      } else {
        // National/International business - generate industry-wide research
        response = await api.post(`/market-intelligence/industries/${industryId}/generate`, {
          userId: currentUser?.id || null,
          userBrandId: activeBrand?.userBrandId || null
        })
      }

      console.log('Generate response:', response.data)

      // If already completed, reload immediately
      if (response.data.status === 'completed') {
        console.log('Report already exists, reloading page...')
        window.location.reload()
        return
      }

      // If processing, poll for completion
      if (response.data.status === 'processing') {
        console.log('Report generation started, polling for completion...')
        await pollMarketReportStatus()
        console.log('Report generated successfully, reloading page...')
        window.location.reload()
      }
    } catch (error: any) {
      console.error('Failed to generate market report:', error)
      alert(error.response?.data?.error || error.message || 'Failed to generate market research. Please try again.')
    } finally {
      setGeneratingReport(false)
    }
  }

  // Fetch market intelligence data
  useEffect(() => {
    const fetchMarketIntelligence = async () => {
      // Skip API fetch in demo mode
      if (isDemoMode) {
        setLoading(false)
        return
      }

      if (!activeBrand?.id) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch brand performance (includes industry check)
        const performanceResponse = await api.get(`/market-intelligence/brands/${activeBrand.id}/performance`)

        // Get industry ID from performance response
        const industryIdFromApi = performanceResponse.data.industry?.id

        if (!industryIdFromApi) {
          setError('Please assign an industry to your brand in Settings to view Market Intelligence')
          setLoading(false)
          return
        }

        // Save industry ID for report generation
        setIndustryId(industryIdFromApi)

        // Fetch brand-specific market report (uses localized data if available)
        let reportResponse
        try {
          reportResponse = await api.get(`/market-intelligence/brands/${activeBrand.id}/report`)
        } catch (reportErr: any) {
          // If report doesn't exist (404), set special error to show generate button
          if (reportErr.response?.status === 404) {
            // Capture if this is a local business from the response
            setIsLocalBusiness(reportErr.response?.data?.isLocalBusiness || false)
            setError('no-report')
            setLoading(false)
            return
          }
          throw reportErr // Re-throw other errors
        }

        // Transform API data to match component expectations
        const transformedData = {
          industry: {
            name: reportResponse.data.report.industry?.name || 'Unknown',
            slug: reportResponse.data.report.industry?.slug || 'unknown'
          },
          // Location data for localized reports
          location: reportResponse.data.report.location || null,
          isLocalized: reportResponse.data.report.isLocalized || false,
          marketOverview: {
            estimatedMonthlySearches: reportResponse.data.report.marketData.estimatedMonthlySearches,
            marketSize: reportResponse.data.report.marketData.marketSizeEstimate,
            totalMarketQueries: reportResponse.data.report.queries?.length || 0,
            marketTrend: reportResponse.data.report.marketData.marketTrend,
            trendPercentage: reportResponse.data.report.marketData.trendPercentage,
            lastUpdated: new Date(reportResponse.data.report.generatedAt).toLocaleDateString()
          },
          brandPerformance: {
            brandName: activeBrand.companyName,
            coveragePercentage: performanceResponse.data.performance.brandCoveragePercentage,
            industryCoverageAverage: performanceResponse.data.performance.industryAvgCoveragePercentage,
            trackedQueries: performanceResponse.data.performance.trackedQueries,
            untrackedQueries: performanceResponse.data.performance.untrackedQueries,
            mentionRate: performanceResponse.data.performance.brandMentionRate,
            industryMentionRateAverage: performanceResponse.data.performance.industryAvgMentionRate,
            rankInIndustry: performanceResponse.data.performance.rankInIndustry,
            totalBrandsInIndustry: performanceResponse.data.performance.totalBrandsInIndustry
          },
          topMarketQueries: reportResponse.data.report.queries?.slice(0, 8).map((q: any) => ({
            query: q.query,
            volume: q.estimatedVolume,
            buyingSignal: q.buyingSignal,
            tracked: false, // TODO: Check if tracked
            priority: q.priority
          })) || [],
          buyingSignals: reportResponse.data.report.buyingSignals || [],
          marketTrends: reportResponse.data.report.trends?.map((t: any) => ({
            trend: t.name,
            direction: t.direction.toLowerCase(),
            change: t.changePercentage,
            description: t.description
          })) || [],
          journeyInsights: {
            averageResearchDuration: reportResponse.data.report.marketData.averageResearchDuration,
            topDecisionFactors: reportResponse.data.report.marketData.topDecisionFactors,
            priceSensitivity: reportResponse.data.report.marketData.priceSensitivity,
            urgencyLevel: reportResponse.data.report.marketData.urgencyLevel
          },
          competitiveGaps: [], // TODO: Implement competitive gaps
          sources: reportResponse.data.report.sources || [], // Perplexity citations
          generatedBy: reportResponse.data.report.generatedBy || 'AI'
        }

        setMarketData(transformedData)
        setLoading(false)

      } catch (err: any) {
        console.error('Error fetching market intelligence:', err)
        setError(err.response?.data?.error || 'Failed to load market intelligence')
        setLoading(false)
      }
    }

    fetchMarketIntelligence()
  }, [activeBrand?.id])

  // Use fetched data or fall back to demo data
  const data = marketData || DEMO_DATA

  const handleAddQuery = (query: string) => {
    console.log('Adding query:', query)
    // TODO: Implement add to tracking
  }

  return (
    <div className="space-y-6">

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading Market Intelligence...</p>
          </div>
        </div>
      )}

      {/* Error/No Report State */}
      {!loading && error && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 p-8 rounded-lg shadow-md">
          <div className="flex items-start gap-4">
            <AlertCircle className="w-8 h-8 text-orange-500 flex-shrink-0 mt-1" />
            <div>
              {error === 'no-report' ? (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Market Research Available</h3>
                  <p className="text-gray-700 mb-4">
                    Market research hasn't been generated for your industry yet.{import.meta.env.DEV ? ' Click below to generate a comprehensive market intelligence report.' : ' Market research will be generated automatically based on your subscription schedule.'}
                  </p>
                  {/* Generate button only shown in development */}
                  {import.meta.env.DEV && (
                    <button
                      onClick={generateMarketReport}
                      disabled={generatingReport}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {generatingReport ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating Report...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4" />
                          Generate Market Research
                        </>
                      )}
                    </button>
                  )}
                </>
              ) : (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Industry Assigned</h3>
                  <p className="text-gray-700 mb-4">{error}</p>
                  <a
                    href="/settings"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Go to Settings
                    <ArrowUp className="w-4 h-4 rotate-45" />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Industry & Location Header */}
      {!loading && !error && data && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Industry & Location Info */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-indigo-200" />
                <span className="text-indigo-200 text-sm font-medium uppercase tracking-wide">Market Analysis</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                {data.industry.name}
                {data.isLocalized && data.location?.name && (
                  <span className="text-indigo-200"> in {data.location.name}</span>
                )}
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                {/* Location Badge */}
                {data.isLocalized ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>Local Market</span>
                    {data.location?.serviceAreas?.length > 1 && (
                      <span className="text-indigo-200">
                        • {data.location.serviceAreas.length} areas
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full text-sm">
                    <Globe className="w-4 h-4" />
                    <span>National Market</span>
                  </div>
                )}
                {/* Last Updated */}
                <span className="text-indigo-200 text-sm">
                  Updated {data.marketOverview.lastUpdated}
                </span>
              </div>
            </div>

            {/* Service Areas (for local businesses) */}
            {data.isLocalized && data.location?.serviceAreas?.length > 0 && (
              <div className="bg-white/10 rounded-lg p-4 max-w-sm">
                <p className="text-xs text-indigo-200 mb-2 font-medium uppercase tracking-wide">Service Areas</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.location.serviceAreas.slice(0, 6).map((area: string, idx: number) => (
                    <span key={idx} className="px-2 py-0.5 bg-white/20 rounded text-xs">
                      {area}
                    </span>
                  ))}
                  {data.location.serviceAreas.length > 6 && (
                    <span className="px-2 py-0.5 text-indigo-200 text-xs">
                      +{data.location.serviceAreas.length - 6} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content - Only show when data is available */}
      {!loading && !error && data && (
        <>


      {/* Market Intelligence Insights */}
      <MarketIntelligenceInsights />

      {/* Market Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Market Trend */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 text-green-500" />
          </div>
          <p className="text-sm text-gray-600 mb-1">Market Trend</p>
          <p className="text-2xl font-bold text-green-600">{stripCitations(data.marketOverview.marketTrend)}</p>
          <p className="text-xs text-gray-500 mt-1">{stripCitations(data.marketOverview.trendPercentage)} YoY</p>

          {/* Sources */}
          {(() => {
            const sources = getSourcesForText(data.marketOverview.marketTrend, data.marketOverview.trendPercentage)
            return sources.length > 0 && (
              <div className="pt-3 mt-3 border-t border-gray-200 space-y-1">
                <p className="text-xs text-gray-400">Sources:</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {sources.map((source: string, idx: number) => (
                    <a
                      key={idx}
                      href={source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-blue-400 hover:text-blue-600 hover:underline"
                      title={source}
                    >
                      {new URL(source).hostname.replace('www.', '')}
                    </a>
                  ))}
                </div>
              </div>
            )
          })()}
        </div>

        {/* Monthly Searches */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <Search className="w-8 h-8 text-blue-500" />
          </div>
          <p className="text-sm text-gray-600 mb-1">Est. Monthly Searches</p>
          <p className="text-2xl font-bold text-gray-900">{stripCitations(data.marketOverview.estimatedMonthlySearches)}</p>

          {/* Sources */}
          {(() => {
            const sources = getSourcesForText(data.marketOverview.estimatedMonthlySearches)
            return sources.length > 0 && (
              <div className="pt-3 mt-3 border-t border-gray-200 space-y-1">
                <p className="text-xs text-gray-400">Sources:</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {sources.map((source: string, idx: number) => (
                    <a
                      key={idx}
                      href={source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-blue-400 hover:text-blue-600 hover:underline"
                      title={source}
                    >
                      {new URL(source).hostname.replace('www.', '')}
                    </a>
                  ))}
                </div>
              </div>
            )
          })()}
        </div>

        {/* Market Size */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-purple-500" />
          </div>
          <p className="text-sm text-gray-600 mb-1">Market Size</p>
          <p className="text-2xl font-bold text-gray-900">{stripCitations(data.marketOverview.marketSize).split(' ')[0]}</p>
          {/* <p className="text-xs text-gray-500 mt-1">potential buyers</p> /*}

          {/* Sources */}
          {(() => {
            const sources = getSourcesForText(data.marketOverview.marketSize)
            return sources.length > 0 && (
              <div className="pt-3 mt-3 border-t border-gray-200 space-y-1">
                <p className="text-xs text-gray-400">Sources:</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {sources.map((source: string, idx: number) => (
                    <a
                      key={idx}
                      href={source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-blue-400 hover:text-blue-600 hover:underline"
                      title={source}
                    >
                      {new URL(source).hostname.replace('www.', '')}
                    </a>
                  ))}
                </div>
              </div>
            )
          })()}
        </div>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Buying Signals Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-indigo-600" />
            Market Buying Signals
          </h3>
          <p className="text-xs text-gray-600 mb-4">
            Distribution based on analysis of {data.topMarketQueries.length * 6} market queries
          </p>
          <div className="space-y-4">
            {data.buyingSignals.map((signal: any, idx: number) => (
              <div key={idx}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{signal.signal}</span>
                  <span className="text-sm font-bold text-indigo-600">{signal.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                  <div
                    className={`h-2 rounded-full ${
                      idx === 0 ? 'bg-blue-500' : idx === 1 ? 'bg-purple-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${signal.percentage}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500">{signal.description}</p>
              </div>
            ))}
          </div>

          {/* Sources */}
          {data.sources && data.sources.length > 0 && (
            <div className="pt-4 mt-4 border-t border-gray-200 space-y-1">
              <p className="text-xs text-gray-400">Sources:</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {data.sources.slice(0, 3).map((source: string, idx: number) => (
                  <a
                    key={idx}
                    href={source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-blue-400 hover:text-blue-600 hover:underline"
                    title={source}
                  >
                    {new URL(source).hostname.replace('www.', '')}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Customer Journey Insights */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-500" />
            Journey Insights
          </h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Research Duration</p>
              <p className="text-lg font-semibold text-gray-900">
                {stripCitations(data.journeyInsights.averageResearchDuration)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-2">Top Decision Factors</p>
              <div className="space-y-1">
                {data.journeyInsights.topDecisionFactors.map((factor: any, idx: number) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-gray-700">{stripCitations(factor)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-sm text-gray-600">Price Sensitivity</span>
              <span className={`text-sm font-semibold ${
                data.journeyInsights.priceSensitivity === 'High' ? 'text-red-600' : 'text-yellow-600'
              }`}>
                {stripCitations(data.journeyInsights.priceSensitivity)}
              </span>
            </div>

            {/* Journey Insights Sources */}
            {(() => {
              const sources = getSourcesForText(
                data.journeyInsights.averageResearchDuration,
                ...(data.journeyInsights.topDecisionFactors || []),
                data.journeyInsights.priceSensitivity,
                data.journeyInsights.urgencyLevel
              )
              return sources.length > 0 && (
                <div className="pt-3 mt-3 border-t border-gray-200 space-y-1">
                  <p className="text-xs text-gray-400">Sources:</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {sources.map((source: string, idx: number) => (
                      <a
                        key={idx}
                        href={source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-blue-400 hover:text-blue-600 hover:underline"
                        title={source}
                      >
                        {new URL(source).hostname.replace('www.', '')}
                      </a>
                    ))}
                  </div>
                </div>
              )
            })()}
          </div>
        </div>

        {/* Market Coverage - Brand vs Industry
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-orange-500" />
            Coverage Comparison
          </h3>
          <div className="space-y-6">
            {/* Dual Coverage Circles
            <div className="grid grid-cols-2 gap-4">
              {/* Your Brand
              <div className="text-center">
                <p className="text-xs font-medium text-gray-600 mb-2">Your Brand</p>
                <div className="relative w-24 h-24 mx-auto">
                  <svg className="w-24 h-24 transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-gray-200"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - data.brandPerformance.coveragePercentage / 100)}`}
                      className="text-orange-500 transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold text-gray-900">
                      {data.brandPerformance.coveragePercentage}%
                    </span>
                  </div>
                </div>
              </div>
              {/* Industry Average
              <div className="text-center">
                <p className="text-xs font-medium text-gray-600 mb-2">Industry Avg</p>
                <div className="relative w-24 h-24 mx-auto">
                  <svg className="w-24 h-24 transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-gray-200"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - data.brandPerformance.industryCoverageAverage / 100)}`}
                      className="text-indigo-500 transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold text-gray-900">
                      {data.brandPerformance.industryCoverageAverage}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2 text-sm border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">You Track</span>
                <span className="font-semibold text-green-600">
                  {data.brandPerformance.trackedQueries} queries
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Untapped</span>
                <span className="font-semibold text-orange-600">
                  {data.brandPerformance.untrackedQueries} queries
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Industry Total</span>
                <span className="font-semibold text-gray-900">
                  {data.marketOverview.totalMarketQueries} queries
                </span>
              </div>
            </div>
          </div>
        </div>
         */}

      </div>


      <div>
        {/* Competitive Gaps}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-orange-500" />
            Competitive Query Gaps
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Queries your competitors rank for but you're not tracking
          </p>
          <div className="space-y-3">
            {data.competitiveGaps.map((gap, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 mb-2">{gap.query}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Competitors:</span>
                      {gap.competitors.map((comp, idx) => (
                        <span
                          key={idx}
                          className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700"
                        >
                          {comp}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleAddQuery(gap.query)}
                    className="text-indigo-600 hover:text-indigo-700 flex-shrink-0"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        */}

        {/* Market Trends */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-green-500" />
            Market Trends
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Emerging and declining search patterns in your market
          </p>
          <div className="space-y-3">
            {data.marketTrends.map((trend: any, idx: number) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-4">
                    {/* Percentage Badge - fixed width for alignment */}
                    <div className="flex-shrink-0">
                      <span className={`inline-flex flex-col items-center justify-center px-3 py-1.5 rounded-md text-base font-bold ${
                        trend.direction === 'rising' || trend.direction === 'up'
                          ? 'bg-green-500 text-white'
                          : trend.direction === 'declining' || trend.direction === 'down'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}>
                        {(() => {
                          const changeText = stripCitations(trend.change)
                          if (changeText.toUpperCase().includes('CAGR')) {
                            const [percent] = changeText.split(/\s+/)
                            return (
                              <>
                                <span>{percent}</span>
                                {/* <span className="text-xs font-normal mt-0.5">CAGR</span> */}
                              </>
                            )
                          }
                          return changeText
                        })()}
                      </span>
                    </div>

                    {/* Trend info - aligned because badge area is fixed width */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {(trend.direction === 'rising' || trend.direction === 'up') ? (
                          <ArrowUp className="w-4 h-4 text-green-500" />
                        ) : (trend.direction === 'declining' || trend.direction === 'down') ? (
                          <ArrowDown className="w-4 h-4 text-red-500" />
                        ) : (
                          <Minus className="w-4 h-4 text-gray-400" />
                        )}
                        <p className="text-sm font-medium text-gray-900">{stripCitations(trend.trend).replace(/^[—\-]\s*/, '')}</p>
                      </div>
                      <p className="text-xs text-gray-500">{stripCitations(trend.description)}</p>
                    </div>
                  </div>
                </div>
            ))}
          </div>

          {/* Market Trends Sources */}
          {(() => {
            const allTrendTexts = data.marketTrends.flatMap((t: any) => [t.trend, t.change, t.description])
            const sources = getSourcesForText(...allTrendTexts)
            return sources.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-1">
                  <p className="text-xs text-gray-400">Sources:</p>
                  <div className="flex flex-wrap gap-x-2 gap-y-1">
                    {sources.map((source: string, idx: number) => (
                        <a
                            key={idx}
                            href={source}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-blue-400 hover:text-blue-600 hover:underline"
                            title={source}
                        >
                          {new URL(source).hostname.replace('www.', '')}
                        </a>
                    ))}
                  </div>
                </div>
            )
          })()}
        </div>
      </div>

      {/* Top Market Queries */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            Market Query Opportunities
          </h3>
          <span className="text-sm text-gray-500">
            {data.topMarketQueries.length} opportunities identified
          </span>
        </div>

        <div className="space-y-3">
          {data.topMarketQueries.map((item: any, idx: number) => (
            <div
              key={idx}
              className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <p className="text-sm font-medium text-gray-900 break-words">"{item.query}"</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:gap-4 ml-7">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      item.volume === 'High'
                        ? 'bg-red-100 text-red-700'
                        : item.volume === 'Medium'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {item.volume} Volume
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {item.buyingSignal}
                    </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      item.priority === 'High'
                        ? 'bg-purple-100 text-purple-700'
                        : item.priority === 'Medium'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {item.priority} Priority
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleAddQuery(item.query)}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors flex-shrink-0 w-full md:w-auto mt-2 md:mt-0"
                >
                  <Plus className="w-4 h-4" />
                  Add to Tracking
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      </>
    )}
    </div>
  )
}
